import { create } from 'zustand';

/**
 * Single-Engine Player Architecture
 * 
 * This store manages ALL player state. There is only ONE audio element
 * and ONE video element in the entire app (in MediaEngine.jsx).
 * All UI components are just "remotes" that read/write this store.
 * 
 * PLAYBACK SESSION:
 * Only one device can play at a time per account. When playback starts,
 * a session is created on the server. Heartbeats keep it alive.
 * If another device starts playing, this session is invalidated.
 */

export const usePlayerStore = create((set, get) => ({
  // ========== TRACK STATE ==========
  currentTrack: null,
  queue: [],
  
  // ========== PLAYBACK STATE ==========
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  mediaError: null,  // Error message if media fails to load
  _forceStop: false, // Flag to force MediaEngine to stop immediately
  
  // ========== SESSION STATE ==========
  sessionToken: null,
  sessionValid: true,
  sessionError: null,
  
  // ========== UI STATE ==========
  isMinimized: false,       // Audio: unused – kept for backward compat only
  isVideoExpanded: false,   // Video: overlay mode (not fullscreen)
  showQueue: false,
  isVideoInPage: false,     // true while VideoPlayer has MediaEngine's <video> positioned
  trialExpiredPaywall: false, // true when free-trial episode reaches 80% → show paywall
  
  // ========== MINI PLAYER POSITION & SIZE (for draggable/resizable) ==========
  miniPlayerPosition: { x: null, y: null }, // null = default position
  miniPlayerSize: { width: 320, height: 180 }, // 16:9 ratio

  // ========== SESSION ACTIONS ==========
  
  setSessionToken: (token) => set({ sessionToken: token, sessionValid: true, sessionError: null }),
  
  setSessionInvalid: (error) => set({ 
    sessionValid: false, 
    sessionError: error,
    isPlaying: false 
  }),
  
  clearSessionError: () => set({ sessionError: null }),

  // ========== ACTIONS ==========
  
  // Set current track (this triggers MediaEngine to load new src)
  setCurrentTrack: (track) => {
    console.log('[playerStore] setCurrentTrack called with:', track?.title, track?.media_type);
    // Ignore video tracks - they should be played in VideoPlayer page, not MediaEngine
    if (track?.media_type === 'video') {
      console.log('[playerStore] Ignoring video track in setCurrentTrack');
      return;
    }
    set({ 
      currentTrack: track,
      currentTime: 0,
      duration: 0,
    });
  },
  
  // Play a track immediately
  playTrack: (track) => {
    const { currentTrack } = get();
    
    // VIDEO tracks should NOT be played in MediaEngine - they have dedicated VideoPlayer page
    if (track.media_type === 'video') {
      console.log('[playerStore] Ignoring video track - use VideoPlayer page instead');
      return;
    }
    
    // If same track, just toggle play state
    if (currentTrack?.id === track.id) {
      set({ isPlaying: !get().isPlaying });
      return;
    }
    
    // New track - session will be created in MediaEngine
    set({ 
      currentTrack: track,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      isMinimized: false,
      isVideoExpanded: false,
      sessionToken: null, // Clear old session, new one will be created
      sessionValid: true,
      sessionError: null,
      mediaError: null,  // Clear previous media error
    });
  },
  
  // Toggle play/pause
  togglePlay: () => set(state => ({ isPlaying: !state.isPlaying })),
  
  // Set playing state directly
  setIsPlaying: (playing) => set({ isPlaying: playing }),
  
  // Stop playback completely
  stop: () => set({ 
    isPlaying: false,
  }),
  
  // Clear current track (stop and remove from state)
  clearCurrentTrack: () => set({
    currentTrack: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    isMinimized: false,
    isVideoExpanded: false,
    _forceStop: true, // Signal MediaEngine to stop immediately
  }),
  
  // Acknowledge force stop (called by MediaEngine after processing)
  clearForceStop: () => set({ _forceStop: false }),
  
  // Flag that VideoPlayer is actively positioning MediaEngine's video element.
  // When true, MediaEngine clears its inline style so VideoPlayer can drive position via CSS.
  setVideoInPage: (val) => set({ isVideoInPage: val }),

  // Start a video track in MediaEngine (bypasses the video-type guard).
  // Used by VideoPlayer to hand off playback to MediaEngine for seamless mini-player transitions.
  startVideoTrack: (track) => {
    set({
      currentTrack: { ...track },
      currentTime: 0,
      duration: track.duration || 0,
      isPlaying: true,
      isMinimized: false,
      isVideoExpanded: false,
      sessionToken: null,
      sessionValid: true,
      sessionError: null,
      mediaError: null,
    });
  },

  // Set current track for mini player (when navigating away from video page).
  // keepPlaying mirrors the play state at the moment the user left the video page,
  // so the mini player continues from exactly where the user left off.
  setCurrentTrackForMiniPlayer: (track) => {
    console.log('[playerStore] setCurrentTrackForMiniPlayer called with:', track?.title, track?.media_type, 'keepPlaying:', track?.keepPlaying);
    // isPlaying is always set to false here — MediaEngine will call setIsPlaying(true)
    // itself AFTER seeking to resumeFrom, so the video never plays from position 0
    // and handleTimeUpdate never writes 0 to the store before the seek completes.
    set({
      currentTrack: track,
      currentTime: track.resumeFrom || 0,
      duration: track.duration || 0,
      isPlaying: false,
      isMinimized: false,
      isVideoExpanded: false,
    });
  },
  
  // Update current time (called by MediaEngine on timeupdate)
  setCurrentTime: (time) => set({ currentTime: time }),
  
  // Update duration (called by MediaEngine on loadedmetadata)
  setDuration: (dur) => set({ duration: dur }),
  
  // Set media error (called by MediaEngine on error)
  setMediaError: (error) => set({ mediaError: error, isPlaying: false }),
  
  // Clear media error
  clearMediaError: () => set({ mediaError: null }),
  
  // Seek to specific time
  seek: (time) => set({ currentTime: time, _seekTo: time }),
  
  // Seek relative (used by components, MediaEngine listens)
  seekRelative: (seconds) => {
    const { currentTime, duration } = get();
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    set({ currentTime: newTime, _seekTo: newTime });
  },
  
  // Clear seek command (called by MediaEngine after processing)
  clearSeekCommand: () => set({ _seekTo: undefined }),
  
  // Volume
  changeVolume: (vol) => set({ volume: vol, isMuted: vol === 0 }),
  toggleMute: () => set(state => ({ isMuted: !state.isMuted })),
  
  // UI State
  setIsMinimized: (min) => set({ isMinimized: min }),
  setIsVideoExpanded: (exp) => set({ isVideoExpanded: exp }),
  setShowQueue: (show) => set({ showQueue: show }),
  setTrialExpiredPaywall: (val) => set({ trialExpiredPaywall: val }),
  
  // Mini player position & size
  setMiniPlayerPosition: (pos) => set({ miniPlayerPosition: pos }),
  setMiniPlayerSize: (size) => set({ miniPlayerSize: size }),
  resetMiniPlayerPosition: () => set({ miniPlayerPosition: { x: null, y: null } }),
  
  // Close player entirely
  closePlayer: () => set({
    currentTrack: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    isVideoExpanded: false,
    isMinimized: false,
    showQueue: false,
    sessionToken: null,
    sessionValid: true,
    sessionError: null,
  }),
  
  // ========== QUEUE ACTIONS ==========
  
  addToQueue: (track) => {
    const { queue } = get();
    // Don't add duplicates
    if (queue.some(t => t.id === track.id)) return;
    set({ queue: [...queue, track] });
  },
  
  removeFromQueue: (trackId) => {
    const { queue } = get();
    set({ queue: queue.filter(t => t.id !== trackId) });
  },
  
  clearQueue: () => set({ queue: [] }),

  // Reorder queue by moving item from one index to another (drag-and-drop)
  moveInQueue: (fromIndex, toIndex) => {
    const { queue } = get();
    if (fromIndex === toIndex) return;
    const next = [...queue];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    set({ queue: next });
  },

  // Play a specific item from the queue immediately (removes it from queue)
  playFromQueue: (index) => {
    const { queue } = get();
    if (index < 0 || index >= queue.length) return;
    const track = queue[index];
    const newQueue = queue.filter((_, i) => i !== index);
    set({
      queue: newQueue,
      currentTrack: track,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      isVideoExpanded: false,
      sessionToken: null,
      sessionValid: true,
      sessionError: null,
      mediaError: null,
    });
  },

  // Skip to next in queue
  skipNext: () => {
    const { queue } = get();
    if (queue.length === 0) {
      set({ isPlaying: false });
      return;
    }
    
    const [nextTrack, ...restQueue] = queue;
    set({
      queue: restQueue,
      currentTrack: nextTrack,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      isVideoExpanded: false,
      sessionToken: null, // New track, new session needed
    });
  },
  
  // Play next automatically (called on track end)
  playNext: () => {
    const { queue } = get();
    if (queue.length === 0) return;
    
    const [nextTrack, ...restQueue] = queue;
    set({
      queue: restQueue,
      currentTrack: nextTrack,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      sessionToken: null, // New track, new session needed
    });
  },
}));

// Selector hooks for performance
export const useCurrentTrack = () => usePlayerStore(state => state.currentTrack);
export const useIsPlaying = () => usePlayerStore(state => state.isPlaying);
export const useSessionError = () => usePlayerStore(state => state.sessionError);
export const usePlayerUI = () => usePlayerStore(state => ({
  isMinimized: state.isMinimized,
  isVideoExpanded: state.isVideoExpanded,
  showQueue: state.showQueue,
}));
